import { ShieldX, RefreshCw, Key, Check, Copy, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

/**
 * Disable 2FA Dialog
 */
export function DisableAuthDialog({
  open,
  onOpenChange,
  disableCode,
  setDisableCode,
  actionLoading,
  error,
  onDisable,
  onClearError
}) {
  const handleClose = () => {
    onOpenChange(false);
    setDisableCode('');
    onClearError();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-red-600">
            <ShieldX className="w-5 h-5" />
            <span>Deaktiver autentiseringsapp</span>
          </DialogTitle>
          <DialogDescription>
            Skriv inn en kode fra autentiseringsappen din for å bekrefte.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">
              Advarsel: Deaktivering av 2FA vil gjøre kontoen din mindre sikker.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="disable-code">Verifiseringskode</Label>
            <Input
              id="disable-code"
              placeholder="000 000"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              className="text-center text-2xl tracking-widest"
              maxLength={7}
            />
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Avbryt
          </Button>
          <Button 
            variant="destructive"
            onClick={onDisable}
            disabled={disableCode.replace(/\s/g, '').length < 6 || actionLoading}
          >
            {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Deaktiver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Regenerate Backup Codes Dialog
 */
export function RegenerateBackupDialog({
  open,
  onOpenChange,
  regenerateCode,
  setRegenerateCode,
  actionLoading,
  error,
  onRegenerate,
  onClearError
}) {
  const handleClose = () => {
    onOpenChange(false);
    setRegenerateCode('');
    onClearError();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <RefreshCw className="w-5 h-5 text-teal-600" />
            <span>Generer nye backup-koder</span>
          </DialogTitle>
          <DialogDescription>
            De gamle backup-kodene vil bli ugyldige.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="regenerate-code">Verifiseringskode</Label>
            <Input
              id="regenerate-code"
              placeholder="000 000"
              value={regenerateCode}
              onChange={(e) => setRegenerateCode(e.target.value)}
              className="text-center text-2xl tracking-widest"
              maxLength={7}
            />
            <p className="text-xs text-slate-500">
              Skriv inn en kode fra autentiseringsappen din
            </p>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Avbryt
          </Button>
          <Button 
            onClick={onRegenerate}
            disabled={regenerateCode.replace(/\s/g, '').length < 6 || actionLoading}
          >
            {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Generer nye koder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Display Backup Codes Dialog
 */
export function BackupCodesDisplayDialog({
  open,
  onOpenChange,
  backupCodes,
  copiedIndex,
  onCopyToClipboard,
  onCopyAllBackupCodes
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Key className="w-5 h-5 text-teal-600" />
            <span>Nye backup-koder</span>
          </DialogTitle>
          <DialogDescription>
            Lagre disse kodene på et trygt sted.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-600">
              De gamle backup-kodene er nå ugyldige. Kun disse nye kodene vil fungere.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, i) => (
              <div 
                key={i}
                className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded font-mono text-sm"
              >
                <span>{code}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => onCopyToClipboard(code, i)}
                >
                  {copiedIndex === i ? (
                    <Check className="w-3 h-3 text-emerald-500" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </Button>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={onCopyAllBackupCodes}
          >
            {copiedIndex === 'all' ? (
              <>
                <Check className="w-4 h-4 mr-2 text-emerald-500" />
                Kopiert!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Kopier alle koder
              </>
            )}
          </Button>
        </div>
        <DialogFooter>
          <Button 
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Ferdig
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
